<template>
  <info-panel
    v-if="relation"
    :item="relation"
    :label="relation ? $t(`relationUpdateTypes.${relation?.updateType}`) : ''"
    :title="
      $t('headings.relation', {
        startElement: startElement?.title || `[${$t('labels.unnamed')}]`,
        endElement: endElement?.title || `[${$t('labels.unnamed')}]`
      })
    "
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{
      $t("labels.showEmptySections")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template #description v-if="settingsValues.showEmptySections || !isEmpty(relation.description)">
      <formatted-read-more-text
        :value="relation.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <!-- <template #label-expression>
      {{ $t("words.formula") }}
    </template>
    <template #expression>
      <expression-rendered :expression="valueExpression" :parse-context="parseContext" class="mb-2" />
    </template> -->

    <!-- ELEMENTS -->
    <template #label-elements>
      {{ $t("objectNames.element", 2) }}
    </template>
    <template
      #elements
      v-if="settingsValues.showEmptySections || (startElement && endElement)"
    >
      <div class="relation-infopanel__elementsrow">
        <div>
          <div class="label">
            <template v-if="endElement?.type == 'resource'">
              {{ $t("objectNames.element", 1) }}
            </template>
            <template v-else>{{ $t("labels.startElement") }}</template>
          </div>
          <button-item v-if="startElement" @click="openOverlay(startElement)">
            <span v-if="startElement.title">
              {{ startElement.title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-item>
        </div>
        <lucide-icon name="arrow-right" class="relation-infopanel__elementsrow-arrow" />
        <div>
          <div class="label">
            <template v-if="endElement?.type == 'resource'">
              {{ $t("objectNames.resource", 1) }}
            </template>
            <template v-else>{{ $t("labels.endElement") }}</template>
          </div>
          <button-item v-if="endElement" @click="openOverlay(endElement)">
            <span v-if="endElement.title">
              {{ endElement.title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-item>
        </div>
      </div>
    </template>

    <template #label-impactEffect>
      {{ $t("labels.impactEffect") }}
    </template>
    <template #label-_strength>{{ $t("labels.relationStrength") }}</template>
    <template #label-_curve>
      {{ $t("labels.influenceCurve") }}
    </template>
    <template #label-_delay>{{ $t("labels.delay") }}</template>
    <template
      #impactEffect
      v-if="
        settingsValues.showEmptySections ||
        relation.updateType == 'none' ||
        sections._strength ||
        sections._curve ||
        sections._delay
      "
    >
      <template v-if="relation.updateType == 'none'">
        {{
          $t("texts.isNonRelation", [startElement?.title, endElement?.title])
        }}
      </template>
      <template v-else>
        <!-- STRENGTH OF RELATION -->
        <template v-if="sections._strength">
          <h4 class="label">{{ $t("labels.relationStrength") }}</h4>
          <div>
            <i18n-t keypath="texts.updateOnChange" tag="div" scope="global">
              <strong>{{ $t("texts.updateOnChangeBold") }}</strong>
              <em>{{ startElement?.title }}</em>
            </i18n-t>
          </div>
          <div class="mv-2">
            {{ $t("labels.maxInfluence") }}{{ $t(":") }} {{ maxInfluence }} {{
              endElement?.unit
            }}
          </div>
          <div v-if="relation.docs.maxInfluence" class="comment">
            <lucide-icon name="doc" />
            <formatted-text :value="relation.docs.maxInfluence" />
          </div>
        </template>

        <!-- INFLUENCE CURVE -->
        <template v-if="sections._curve">
          <h4 class="label">{{ $t("labels.influenceCurve") }}</h4>
          <div class="relation-infopanel__curverow">
            <input-curve
              style="width: 20rem"
              :value="Number(startElementCalculatedValue?.value)"
              :x-min="startElementCalculatedValue?.minValue ?? null"
              :x-max="startElementCalculatedValue?.maxValue ?? null"
              :point-list="curvePoints"
              :type="curveType"
              :type-editable="false"
              :curve-editable="false"
              :formula="relation.formula"
              @hovervalue="updateCurveHover($event)"
            />
            <div>
              <p class="mt-0">
                {{ $t("labels.xAxis") }}{{ $t(":") }}
                {{
                  $t("labels.valueOfX", [
                    startElement?.title || $t("labels.unnamed")
                  ])
                }}<br />
                {{ $t("labels.yAxis") }}{{ $t(":") }}
                {{
                  $t("labels.influenceOnX", [
                    endElement?.title || $t("labels.unnamed")
                  ])
                }}
              </p>

              <i18n-t
                keypath="texts.influenceCurveExample"
                tag="p"
                scope="global"
              >
                <template #startElement>
                  <em>{{ startElement?.title || $t("labels.unnamed") }}</em>
                </template>
                <template v-if="startElement?.unit" #startUnit
                  > {{ startElement.unit }}
                </template>
                <template #endElement>
                  <em>{{ endElement?.title || $t("labels.unnamed") }}</em>
                </template>
                <template v-if="endElement?.unit" #endUnit
                  > {{ endElement.unit }}
                </template>
                <template #startValueA>
                  {{ curveHover.x2 }}
                </template>
                <template #startValueB> {{ curveHover.x }}</template>
                <template #impulseValue>
                  {{ $n(curveHover.y - curveHover.y2 || 0, "displaySign") }}
                </template>
              </i18n-t>
              <div v-if="curveCommentExists" class="comment">
                <lucide-icon name="doc" />
                <formatted-text :value="relation.docs.curve" />
              </div>
            </div>
          </div>
        </template>

        <template
          v-if="
            sections._delay &&
            (settingsValues.showEmptySections || relation.delay > 0)
          "
        >
          <h4 class="label">{{ $t("labels.delay") }}</h4>
          <div v-if="relation.delay > 0">
            {{ $t("texts.delay", [relation.delay]) }}
          </div>
          <div v-if="relation.delay == 0">
            {{ $t("errors.noX", [$t("labels.delay")]) }}
          </div>
        </template>
      </template>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { isEmpty } from "@/helpers/utility/Text";
import { mapValues } from "@/helpers/utility/CollectionFunctions";

import ButtonItem from "@core/input/ButtonItem.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";
import FormattedText from "@core/ui/FormattedText.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import InputCurve from "./InputCurve.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useRelationStore } from "./relations.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import GraphModel from "./GraphModel";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { CurveFormType, RelationItem } from "./RelationItem";
import type { GraphPoint } from "./GraphPoint";

const props = defineProps<{
  relation: RelationItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const sections = reactive({
  description: true,
  elements: true,
  impactEffect: true,
  _strength: true,
  _curve: true,
  _delay: true
});

const extraSettings = reactive({
  showEmptySections: { type: "check" as const, value: true }
});

const infoPanelStore = useInfoPanelStore();

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.relation = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.relation || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as keyof typeof sections] = savedSections[key];
    }
  }
}

loadSections();

const targets = computed(() => {
  return useTargetStore().items;
});
const resources = computed(() => {
  return useResourceStore().items;
});
const indices = computed(() => {
  return useIndexStore().items;
});
const items = computed(() => {
  return {
    target: targets.value,
    resource: resources.value,
    index: indices.value
  };
});

const startElement = computed(() => {
  if (props.relation == null) return null;
  return items.value[props.relation.startItemType]?.[
    props.relation.startItemId
  ];
});
const endElement = computed(() => {
  if (props.relation == null) return null;
  return items.value[props.relation.endItemType]?.[props.relation.endItemId];
});

// const startElementStartValue = computed(() => {
//   if (props.relation == null) return null;
//   if (startElement.value == null) return null;
//   return useElements().elementValues[startElement.value.uid];
// });
const startElementCalculatedValue = computed(() => {
  if (props.relation == null) return null;
  if (startElement.value == null) return null;
  return useSimulationViewStore().elementValues[startElement.value.uid];
});

// const startValue = computed(() => {
//   if (props.relation == null) return null;
//   const x = Number(startElementStartValue.value?.value ?? 0);
//   console.log(startElementStartValue.value, x);
//   return graphModel.value?.valueAtX(x) ?? null;
// });

// const calculatedValue = computed(() => {
//   if (props.relation == null) return null;
//   // TODO: add relation values to the simulation results.
//   const x = Number(startElementCalculatedValue.value?.value ?? 0);
//   return graphModel.value?.valueAtX(x) ?? null;
// });

// const valueExpression = computed(() => {
//   if (props.relation == null) return "";
//   return useRelationStore().expressions[props.relation.id] ?? "";
// });
// const parseContext = computed(() => {
//   return {
//     rounds: [{ elementValues: useSimulationViewStore().elementValues }]
//   };
// });

// BRANCHES

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

const curvePoints = computed((): GraphPoint[] => {
  if (props.relation == null) return [];
  return props.relation.curvePoints;
});
const curveType = computed((): CurveFormType => {
  if (props.relation == null) return "custom";
  return props.relation.curveType;
});
const graphModel = computed((): GraphModel | null => {
  if (!props.relation) return null;
  return useRelationStore().graphModels[props.relation.id];
});
const maxInfluence = computed((): number => {
  if (!graphModel.value) return 0;
  return graphModel.value.absoluteMaxInfluence;
});
const curveCommentExists = computed((): boolean => {
  if (!props.relation) return false;
  return !isEmpty(props.relation.docs.curve);
});

// EXTRA SETTINGS SAVE

function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.relation = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.relation || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as keyof typeof extraSettings] = {
        ...(savedExtraSettings[key] as any)
      };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();

// ==============================
// CURVE HOVER

const curveHover = reactive({
  x: 0,
  y: 0,
  x2: 0,
  y2: 0
});

function updateCurveHover(data: {
  x: number;
  y: number;
  x2: number;
  y2: number;
}) {
  curveHover.x = data.x;
  curveHover.y = data.y;
  curveHover.x2 = data.x2;
  curveHover.y2 = data.y2;
}
</script>

<style lang="scss">
.relation-infopanel {
  &__elementsrow {
    display: grid;
    grid-template-columns: minmax(5rem, 20rem) 2rem minmax(5rem, 20rem);
    gap: 0.5rem;
    align-items: flex-start;

    &-arrow {
      justify-self: center;
      margin-top: 1.9rem;
    }
  }
  &__curverow {
    display: grid;
    grid-template-columns: 20rem 1fr;
    gap: 1rem;
    align-items: flex-start;
  }
}
</style>
